import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { formatCategoryName } from '../../lib/utils/formatters';
import type { Job } from '../../types/jobs';

interface JobCardProps {
  job: Job & { distance?: number };
  showStatus?: boolean;
  isInactive?: boolean;
}

export function JobCard({ job, showStatus = false, isInactive = false }: JobCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const jobIconUrl = 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/handshake.png';
  const locationIconUrl = 'https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png';

  const handleCardClick = () => {
    navigate(`/jobs/${job.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="transition-all duration-150 cursor-pointer active:scale-[0.98]"
    >
      <div className="aspect-w-16 aspect-h-9 relative rounded-2xl overflow-hidden">
        <div
          className="w-full h-48 flex items-center justify-center"
          style={{ backgroundColor: '#F4F7EE' }}
        >
          <span className="text-3xl font-semibold text-accent-text font-gelica">
            Job
          </span>
        </div>
      </div>
      <div className="pt-4">
          <div className="flex items-center justify-between mb-4">
            {job.user && (
              <Link 
                to={`/profile/${job.user_id}/listings`}
                className="flex items-center space-x-2 group flex-1 min-w-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Avatar 
                  url={job.user.avatar_url} 
                  size="sm" 
                  userId={job.user_id}
                  editable={false}
                />
                <div className="min-w-0">
                  <Username 
                    username={job.user.username || 'Anonymous'}
                    userId={job.user_id}
                    className="text-sm text-content group-hover:text-accent-text truncate"
                  />
                  <p className="text-xs text-content/60 mt-0.5">Posted by</p>
                </div>
              </Link>
            )}
            <div className="flex items-center space-x-2">
              <span className="px-2 py-1 text-xs font-medium bg-accent-base text-accent-text rounded-full">
                {formatCategoryName(job.job_type)}
              </span>
              {isInactive && (
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  Inactive
                </span>
              )}
              {showStatus && job.approval_status === 'pending' && (
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  {t('common.pending')}
                </span>
              )}
            </div>
          </div>

          <h3 className="text-lg font-semibold text-content mb-2">
            {job.title}
          </h3>

          <div className="space-y-2 mb-4">
            <div className="flex items-center text-sm text-content/80">
              <Building2 className="h-4 w-4 mr-2" />
              <span>{job.company_name}</span>
            </div>
            <div className="flex items-center text-sm text-content/80">
              <img src={locationIconUrl} alt="Location" className="h-4 w-4 mr-2" />
              <span>{job.location}</span>
              {job.distance !== undefined && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                  {job.distance < 1
                    ? `${Math.round(job.distance * 1000)}m`
                    : `${job.distance.toFixed(1)}km`}
                </span>
              )}
            </div>
            <div className="flex items-center text-sm text-content/80">
              <img src={jobIconUrl} alt="Job" className="h-4 w-4 mr-2" />
              <span>{job.salary_range}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {job.requirements.slice(0, 3).map((req, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-accent-base text-accent-text rounded-full"
              >
                {req}
              </span>
            ))}
            {job.requirements.length > 3 && (
              <span className="px-2 py-1 text-xs bg-accent-base text-accent-text rounded-full">
                +{job.requirements.length - 3} {t('common.more')}
              </span>
            )}
          </div>
        </div>
    </div>
  );
}
